/**
 * TimeSelect — immersive orb-centred duration + voice picker.
 * Ported from the standalone Calm The Body app (TimeSelectScreen.jsx).
 */
import { useState, useEffect } from 'react';
import {
  listVoices,
  getActiveVoice,
  setVoiceAndPreview,
  preload as preloadVoices,
} from '@/services/calm/audioController';
import type { Protocol } from '@/data/calm/protocols';

const QUICK_TIMES = [3, 5, 7, 10];
const DEFAULT_TIME = 5;
const VOICE_SHORT: Record<string, string> = { female: 'Female', male: 'Male' };
const VOICE_OPTIONS = listVoices();

const CALMING_LABEL: Record<string, string> = {
  panic: 'Calming Panic',
  overwhelm: 'Calming the Storm',
  edgy: 'Calming Restlessness',
  rumination: 'Quieting the Mind',
  cant_sleep: 'Easing into Rest',
  dissociated: 'Coming Back',
  flat: 'Gently Awakening',
  relax: 'Settling',
};

export function TimeSelect({
  protocol,
  stateId,
  onStart,
  onBack,
}: {
  protocol?: Protocol;
  stateId?: string;
  onStart: (opts: { minutes: number; voice: string }) => void;
  onBack: () => void;
}) {
  const [customTime, setCustomTime] = useState(DEFAULT_TIME);
  const [clockOpen, setClockOpen] = useState(false);
  const [protocolOpen, setProtocolOpen] = useState(false);
  const [activePill, setActivePill] = useState(DEFAULT_TIME);
  const [activeVoice, setActiveVoice] = useState<string>(() => getActiveVoice());

  useEffect(() => {
    void preloadVoices();
  }, []);

  const calmingLabel = stateId ? CALMING_LABEL[stateId] || 'Begin' : 'Begin';

  const start = (minutes: number) => onStart({ minutes, voice: activeVoice });

  const adjustTime = (delta: number) => {
    setCustomTime((t) => Math.min(60, Math.max(1, t + delta)));
  };

  const handleVoiceTap = (id: string) => {
    setActiveVoice(id);
    setVoiceAndPreview(id, 0.85);
  };

  return (
    <div className="cb-ts-screen">
      <button className="cb-back" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <div className="cb-ts-heading-area">
        <p className="cb-ts-calming-label">{calmingLabel}</p>
        {protocol && <p className="cb-ts-protocol-hint">{protocol.tagline}</p>}
      </div>

      <div className="cb-ts-orb-area">
        <button
          className="cb-ts-orb"
          onClick={() => start(activePill || DEFAULT_TIME)}
          aria-label={`Start ${activePill || DEFAULT_TIME} minute session`}
        >
          <div className="cb-ts-orb-glow" aria-hidden="true" />
          <span className="cb-ts-orb-label">Start</span>
        </button>
      </div>

      <div className="cb-ts-pills-area">
        <div className="cb-ts-pills-row">
          {QUICK_TIMES.map((m) => (
            <button
              key={m}
              className={`cb-ts-pill${activePill === m ? ' cb-ts-pill--active' : ''}`}
              onClick={() => setActivePill(m)}
              aria-label={`${m} minute${m > 1 ? 's' : ''}`}
            >
              <span className="cb-ts-pill-num">{m}</span>
              <span className="cb-ts-pill-unit">min{m > 1 ? 's' : ''}</span>
            </button>
          ))}
          <button className="cb-ts-pill cb-ts-pill--clock" onClick={() => setClockOpen(true)} aria-label="Choose custom duration">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </button>
        </div>
      </div>

      <div className="cb-ts-voice-area">
        <div className="cb-ts-voice-row">
          {VOICE_OPTIONS.map((v) => (
            <button
              key={v.id}
              className={`cb-ts-voice${activeVoice === v.id ? ' cb-ts-voice--active' : ''}`}
              onClick={() => handleVoiceTap(v.id)}
              aria-label={`Preview ${v.label} voice and use it for the guide`}
            >
              <span className="cb-ts-voice-label">{VOICE_SHORT[v.id] || v.label}</span>
              <span className="cb-ts-voice-unit">voice</span>
            </button>
          ))}
        </div>
      </div>

      {protocol && (
        <div className="cb-ts-alt-area">
          <button className="cb-ts-alt-btn" onClick={() => setProtocolOpen(true)}>
            Breathing Rate Details
          </button>
        </div>
      )}

      {protocolOpen && protocol && (
        <div className="cb-ts-overlay" onClick={() => setProtocolOpen(false)}>
          <div className="cb-ts-overlay-card cb-ts-protocol-card" onClick={(e) => e.stopPropagation()}>
            <div className="cb-ts-overlay-header">
              <p className="cb-ts-overlay-heading">Breathing Rate Details</p>
              <button className="cb-ts-overlay-close" onClick={() => setProtocolOpen(false)} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <p className="cb-ts-protocol-name">{protocol.name}</p>
            <p className="cb-ts-protocol-tagline">{protocol.tagline}</p>
            <div className="cb-intro-phases" style={{ marginTop: 12 }}>
              {protocol.previewSections ? (
                protocol.previewSections.map((section, si) => (
                  <div key={si} className="cb-intro-section">
                    <p className="cb-intro-section-label">{section.label}</p>
                    {section.phases
                      .filter((p) => p.label)
                      .map((phase, i) => (
                        <div key={i} className="cb-intro-phase-row">
                          <span className={`cb-intro-phase-dot cb-dot--${phase.type}`} />
                          <span className="cb-intro-phase-label">{phase.label}</span>
                          <span className="cb-intro-phase-time">{phase.seconds}s</span>
                        </div>
                      ))}
                  </div>
                ))
              ) : (
                protocol.mainPhases
                  .filter((p) => p.label)
                  .map((phase, i) => (
                    <div key={i} className="cb-intro-phase-row">
                      <span className={`cb-intro-phase-dot cb-dot--${phase.type}`} />
                      <span className="cb-intro-phase-label">{phase.label}</span>
                      <span className="cb-intro-phase-time">{phase.seconds}s</span>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      )}

      {clockOpen && (
        <div className="cb-ts-overlay" onClick={() => setClockOpen(false)}>
          <div className="cb-ts-overlay-card" onClick={(e) => e.stopPropagation()}>
            <p className="cb-ts-overlay-heading">Choose duration</p>
            <div className="cb-ts-overlay-picker">
              <button className="cb-ts-stepper" onClick={() => adjustTime(-1)} aria-label="Decrease by 1 minute">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
              <div className="cb-ts-time-circle" aria-live="polite">
                <span className="cb-ts-time-num">{customTime}</span>
                <span className="cb-ts-time-unit">min{customTime > 1 ? 's' : ''}</span>
              </div>
              <button className="cb-ts-stepper" onClick={() => adjustTime(1)} aria-label="Increase by 1 minute">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>
            <button
              className="cb-ts-overlay-start"
              onClick={() => {
                setClockOpen(false);
                start(customTime);
              }}
            >
              Start
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
